import React, { useEffect, useMemo, useState } from 'react';
import { Backdrop, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, Snackbar, Switch, Tab, Tabs, TextField, Tooltip, Typography, Autocomplete } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import trLocale from 'date-fns/locale/tr';
import { format, parseISO } from 'date-fns';
import axios from 'src/utils/axios';
import Alert from 'src/components/Alert';
import ExcelFileCreator from 'src/components/ExcelFileCreator';
import Page from 'src/components/Page';

const styles = {
  filters: (theme) => ({ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), marginBottom: theme.spacing(2), alignItems: 'flex-start' }),
  filterItem: { flex: '1 1 240px', minWidth: 240, maxWidth: 400 },
  searchField: (theme) => ({
    width: 240,
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: 20,
      backgroundColor: theme.palette.background.paper
    }
  }),
  actions: (theme) => ({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }),
  actionButtonsRow: (theme) => ({ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: theme.spacing(1.5) }),
  actionButton: (theme) => ({ borderRadius: 20, textTransform: 'none', fontWeight: 600, paddingLeft: theme.spacing(2.5), paddingRight: theme.spacing(2.5) }),
  excelButton: { backgroundColor: '#1D6F42', color: '#fff', '&:hover': { backgroundColor: '#155a34' } },
  // Temanin warning/error paletlerinde contrastText tanimli olmadigi icin
  // color="warning" / color="error" verilen butonlarda yazi zeminle ayni tona
  // dusup okunmaz hale geliyor. Bu ekrandaki butonlar bu yuzden sabit renkli.
  orangeButton: { backgroundColor: '#FF9800', color: '#fff', '&:hover': { backgroundColor: '#e68900' } },
  orangeOutlineButton: {
    borderColor: '#FF9800',
    color: '#e68900',
    '&:hover': { borderColor: '#e68900', backgroundColor: 'rgba(255, 152, 0, 0.08)' }
  },
  dangerButton: { backgroundColor: '#d32f2f', color: '#fff', '&:hover': { backgroundColor: '#b02525' } },
  resultCount: (theme) => ({ marginBottom: theme.spacing(1) }),
  tableContainer: (theme) => ({ overflow: 'auto', maxHeight: 560, border: `1px solid ${theme.palette.divider}`, borderRadius: 8 }),
  table: { width: '100%', borderCollapse: 'collapse' },
  cell: (theme) => ({ padding: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.divider}`, whiteSpace: 'nowrap', fontSize: 13, textAlign: 'center' }),
  row: (theme) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:hover': { backgroundColor: theme.palette.action.selected }
  }),
  header: (theme) => ({
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.grey[100],
    fontFamily: theme.typography.fontFamily || 'Roboto, Arial, sans-serif',
    fontSize: 13,
    fontWeight: 600,
    zIndex: 1
  }),
  backdrop: (theme) => ({ zIndex: theme.zIndex.drawer + 1, color: '#fff' }),
  // Duzenleme modu switch'i baslik satirinin sagina yerlesir; kapaliyken
  // ekranda bundan baska hicbir ek eleman gorunmez.
  islemCell: (theme) => ({
    padding: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: 92
  }),
  dialogAlan: (theme) => ({ marginTop: theme.spacing(2) }),
  turetilenBilgi: (theme) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 1,
    backgroundColor: theme.palette.action.hover,
    fontSize: 13
  }),
  multiSelectField: {
    // Secim yapildikca kutunun buyumemesi icin: tek satirda kal, tasani gizle.
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '65px !important',
      flexWrap: 'nowrap',
      overflow: 'hidden'
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: 'calc(100% - 16px)'
    },
    '& .MuiAutocomplete-endAdornment': {
      alignItems: 'center',
      display: 'flex',
      top: '50%',
      transform: 'translateY(-50%)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      padding: '4px'
    }
  },
  optionPopper: {
    '& .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: 'rgba(255, 152, 0, 0.12)'
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: 'rgba(255, 152, 0, 0.16)'
    },
    '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
      backgroundColor: 'rgba(255, 152, 0, 0.22)'
    }
  }
};

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedCheckboxIcon = <CheckBoxIcon fontSize="small" style={{ color: '#FF9800' }} />;
const ALL_OPTION = '__ALL__';

const MultiSelectAutocomplete = ({ sx, popperSx, label, options, value, onChange, getOptionLabel, getOptionKey, disabled }) => {
  const allSelected = options.length > 0 && value.length === options.length;
  const selectableOptions = options.length ? [ALL_OPTION, ...options] : [];

  const handleChange = (event, selected, reason, details) => {
    if (details?.option === ALL_OPTION) {
      onChange(allSelected ? [] : options);
      return;
    }
    onChange(selected.filter((item) => item !== ALL_OPTION));
  };

  const label_ = (option) => (option === ALL_OPTION ? 'Tümü' : getOptionLabel(option));

  return (
    <FormControl variant="outlined" fullWidth disabled={disabled}>
      <Autocomplete
        sx={sx}
        multiple
        size="small"
        fullWidth
        disabled={disabled}
        value={value}
        options={selectableOptions}
        componentsProps={{ popper: { sx: popperSx } }}
        getOptionLabel={label_}
        isOptionEqualToValue={(option, val) =>
          option === ALL_OPTION || val === ALL_OPTION ? option === val : getOptionKey(option) === getOptionKey(val)
        }
        onChange={handleChange}
        renderTags={(tagValue, getTagProps) => {
          if (allSelected) {
            return <Chip size="small" label={`Tümü (${tagValue.length})`} {...getTagProps({ index: 0 })} />;
          }
          const visibleCount = 1;
          return (
            <>
              {tagValue.slice(0, visibleCount).map((option, index) => (
                <Chip size="small" key={getOptionKey(option)} label={label_(option)} {...getTagProps({ index })} />
              ))}
              {tagValue.length > visibleCount && (
                <Chip size="small" variant="outlined" label={`+${tagValue.length - visibleCount}`} />
              )}
            </>
          );
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkedCheckboxIcon}
              style={{ marginRight: 8 }}
              checked={option === ALL_OPTION ? allSelected : selected}
            />
            {label_(option)}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label={label} variant="outlined" />}
      />
    </FormControl>
  );
};

// Filtre parametresi: hic secim yoksa da "Tümü" secildiyse de bos gonderilir.
// SP bos parametreyi "filtre yok" olarak yorumluyor; boylece hem sonuc dogru
// oluyor hem de tumu secilince query string sinirini asan uzun CSV olusmuyor.
const csvParam = (selected, options, getValue) => (
  !selected.length || selected.length === options.length
    ? ''
    : selected.map(getValue).join(',')
);

// Ana liste kolonlari: SP alan adi -> ekranda gorunen baslik.
// Excel de bu listeden uretiliyor; SP'nin duzenleme dialogu icin dondurdugu
// ek alanlar (ServisBirimKodu, firma bayraklari, ISO tarih) disarida kaliyor.
const LISTE_COLUMNS = [
  ['BolgeYoneticiligiAdi', 'Bölge Yöneticiliği Adı'],
  ['ServisKodu', 'Servis Kodu'],
  ['ServisAdi', 'Servis Adı'],
  ['ServisUnvani', 'Servis Unvanı'],
  ['StandartNumarasi', 'Standart Numarası'],
  ['StandartAdi', 'Standart Adı'],
  ['BelgeSonGecerlilikTarihi', 'Belge Son Geçerlilik Tarihi'],
  ['Sirket', 'Şirket'],
  ['Il', 'İl']
];

// Firma bilgisi tabloda dort ayri bit kolonunda tutuluyor.
const FIRMA_ALANLARI = [
  ['Arcelik', 'Arçelik'],
  ['Rmk', 'RMK'],
  ['ArcelikLg', 'Arçelik LG'],
  ['Aygaz', 'Aygaz']
];

// Standart listesi yonetmelik esigi tablosundan geliyor; henuz esigi
// tanimlanmamis bir standardin belgesi de girilebilmeli. Serbest yazilan
// deger "TS 10079" formatina normalize ediliyor.
const standartNormalize = (deger) => {
  const temiz = String(deger || '').trim().toLocaleUpperCase('tr').replace(/\s+/g, ' ');
  const eslesme = temiz.match(/^TS\s*(\d+)$/);
  return eslesme ? `TS ${eslesme[1]}` : temiz;
};

// Autocomplete freeSolo modunda secim string olarak da gelebiliyor;
// her iki durumu da tek bir nesne bicimine ceviriyoruz.
const standartNesne = (secim) => {
  if (!secim) return null;
  if (typeof secim === 'string') {
    const standart = standartNormalize(secim);
    return standart ? { Standart: standart, StandartAciklama: '' } : null;
  }
  return secim;
};

const standartEtiket = (option) => {
  const nesne = typeof option === 'string' ? { Standart: option, StandartAciklama: '' } : option;
  if (!nesne) return '';
  return nesne.StandartAciklama
    ? `${nesne.Standart} - ${nesne.StandartAciklama}`
    : nesne.Standart;
};

const BOS_BELGE = {
  servis: null,
  standartlar: [],
  tarih: null,
  Arcelik: false,
  Rmk: false,
  ArcelikLg: false,
  Aygaz: false,
  eski: null
};

const BOS_ESIK = {
  Id: 0,
  sirket: null,
  standart: null,
  standartAdi: '',
  BolgeSayisi: 0,
  BolgeBasinaMinimum: 0,
  ToplamMinimum: 0
};

// Coğrafi bölge kolonlari: SP alan adi -> ekranda gorunen baslik
const BOLGE_COLUMNS = [
  ['Akdeniz', 'Akdeniz'],
  ['DoguAnadolu', 'Doğu Anadolu'],
  ['Ege', 'Ege'],
  ['GuneydoguAnadolu', 'Güneydoğu Anadolu'],
  ['IcAnadolu', 'İç Anadolu'],
  ['Karadeniz', 'Karadeniz'],
  ['Marmara', 'Marmara']
];

// Rapor tablosu: gercek sayilar + yonetmelik esikleri + fark, tek tabloda.
// Excel de bu listeden uretiliyor; SP'nin esik duzenlemesi icin dondurdugu
// ek alanlar (EsikId, SirketId, BolgeSayisi) disarida kaliyor.
const RAPOR_COLUMNS = [
  ['Firma', 'Firma'],
  ['Standart', 'Standart'],
  ['StandartAdi', 'Standart Adı'],
  ['BolgeBasinaMinimum', 'Bölge Başına Min.'],
  ...BOLGE_COLUMNS,
  ['Toplam', 'Toplam'],
  ['ToplamMinimum', 'Toplam Min.'],
  ['ToplamFark', 'Toplam Fark'],
  ['Durum', 'Durum']
];

// Duzenleme yetkisi: su an ekrana erisebilen herkes duzenleyebiliyor.
// Rol kisitlamasi istendiginde degistirilecek tek yer burasi; ornegin
// Apollo Admin ile sinirlamak icin:
//   import { getUser } from 'src/utils/Common';
//   const duzenleyebilir = getUser()?.Role_Id === 1;
// Ayni kontrol backend'de de yapilmali; bu bayrak sadece arayuzu gizler.
const duzenleyebilir = true;

const TseHybSurecleriView = () => {
  const [tab, setTab] = useState(0);
  const [reportRows, setReportRows] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [region, setRegion] = useState([]);
  const [serviceSelection, setServiceSelection] = useState([]);
  const [standartOptions, setStandartOptions] = useState([]);
  const [standartSelection, setStandartSelection] = useState([]);
  const [companySelection, setCompanySelection] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  // --- duzenleme ---
  const [editMode, setEditMode] = useState(false);
  const [allServiceOptions, setAllServiceOptions] = useState([]);
  const [belgeDialog, setBelgeDialog] = useState(null);
  const [belgeSilinecek, setBelgeSilinecek] = useState(null);
  const [esikDialog, setEsikDialog] = useState(null);
  const [esikSilinecek, setEsikSilinecek] = useState(null);

  useEffect(() => {
    setServiceSelection([]);
    if (!region.length) {
      setServiceOptions([]);
      return;
    }

    axios.get('Servisler', {
      params: {
        bolge: region.join(',')
      }
    })
      .then((response) => {
        setServiceOptions(response.data || []);
      })
      .catch(() => {
        setServiceOptions([]);
      });
  }, [region]);

  useEffect(() => {
    axios.get('TseStandartlar')
      .then((response) => setStandartOptions(response.data || []))
      .catch(() => setStandartOptions([]));

    // Bolge secenekleri duz metin listesi olarak tutuluyor; SP tam esleme yapiyor.
    axios.get('TseBolgeler')
      .then((response) => setRegionOptions((response.data || []).map((item) => item.Bolge)))
      .catch(() => setRegionOptions([]));

    axios.get('TseSirketler')
      .then((response) => setCompanyOptions(response.data || []))
      .catch(() => setCompanyOptions([]));
  }, []);

  // Bos birakilan filtre SP tarafinda yok sayilir (= "Tümü").
  const loadRows = () => {
    setLoading(true);
    axios.get('TseHybSurecleri', {
      params: {
        Bolge: csvParam(region, regionOptions, (item) => item),
        BirimKodu: csvParam(serviceSelection, serviceOptions, (item) => item.BirimKodu),
        Standart: csvParam(standartSelection, standartOptions, (item) => item.Standart),
        SirketId: csvParam(companySelection, companyOptions, (item) => item.ID)
      }
    })
      .then((response) => setRows(Array.isArray(response.data) ? response.data : []))
      .catch(() => setNotice({ type: 'error', message: 'TSE-HYB kayıtları alınamadı.' }))
      .finally(() => setLoading(false));
  };

  // Dialogda secilen ama esik tablosunda karsiligi olmayan standartlar:
  // kayit engellenmiyor, kullanici sonucu hakkinda bilgilendiriliyor.
  const tanimsizStandartlar = useMemo(() => (belgeDialog?.standartlar || [])
    .map((item) => item?.Standart)
    .filter((standart) => standart && !standartOptions.some((secenek) => secenek.Standart === standart)),
  [belgeDialog, standartOptions]);

  const filteredRows = useMemo(() => rows.filter((row) =>
    `${row.ServisKodu || ''} ${row.ServisAdi || ''} ${row.ServisUnvani || ''}`.toLocaleLowerCase('tr')
      .includes(search.toLocaleLowerCase('tr'))
  ), [rows, search]);

  // Excel yalnizca ekranda gorunen kolonlari icerir; SP'nin duzenleme icin
  // dondurdugu teknik alanlar disarida kalir.
  const exportRows = () => ExcelFileCreator(
    rows.map((row) => LISTE_COLUMNS.reduce((acc, [field, title]) => {
      acc[title] = row[field];
      return acc;
    }, {})),
    'TSE-HYB Süreçleri'
  );

  // Rapor ekran filtrelerinden bagimsiz; tum veriyi kapsar, bir kez yuklenir.
  const loadReport = () => {
    setLoading(true);
    axios.get('TseHybRapor1')
      .then((response) => setReportRows(Array.isArray(response.data) ? response.data : []))
      .catch(() => setNotice({ type: 'error', message: 'Rapor alınamadı.' }))
      .finally(() => setLoading(false));
  };

  // Tek Excel: ekrandaki tablonun aynisi (mevcut sayilar + yonetmelik esikleri +
  // fark + durum). Basliklar ekrandaki Turkce adlarla yazilir.
  const exportReport = () => ExcelFileCreator(
    reportRows.map((row) => RAPOR_COLUMNS.reduce((acc, [field, title]) => {
      acc[title] = row[field];
      return acc;
    }, {})),
    'TSE-HYB Firma ve Cografi Bolge Bazli Standart Sayilari Raporu'
  );

  const handleTabChange = (event, value) => {
    setTab(value);
    if (value === 1 && reportRows.length === 0) loadReport();
  };

  // Duzenleme modu acilinca dialogun ihtiyaci olan tam servis listesi
  // bir kez yuklenir.
  const handleEditModeChange = (event) => {
    const acik = event.target.checked;
    setEditMode(acik);
    if (!acik) return;
    if (allServiceOptions.length === 0) {
      axios.get('TseHybServisler')
        .then((response) => setAllServiceOptions(Array.isArray(response.data) ? response.data : []))
        .catch(() => setNotice({ type: 'error', message: 'Servis listesi alınamadı.' }));
    }
  };

  // Veri degisti: acik olan listeler tazelenir, rapor cache'i gecersiz kilinir
  // (yoksa duzenleme sonrasi Raporlar sekmesinde eski veri gorunur).
  const veriDegisti = () => {
    setReportRows([]);
    if (rows.length) loadRows();
    if (tab === 1) loadReport();
  };

  const belgeDialogAc = (row) => {
    if (!row) {
      setBelgeDialog({ ...BOS_BELGE });
      return;
    }
    setBelgeDialog({
      servis: allServiceOptions.find((item) => item.BirimKodu === row.ServisBirimKodu)
        || { BirimKodu: row.ServisBirimKodu, Dsc: `${row.ServisKodu} - ${row.ServisAdi || ''}`, TrBolgesi: '', BolgeYoneticiligiAdi: row.BolgeYoneticiligiAdi },
      standartlar: standartOptions.filter((item) => item.Standart === row.StandartNumarasi),
      tarih: row.GecerlilikTarihi ? parseISO(row.GecerlilikTarihi) : null,
      Arcelik: Boolean(row.Arcelik),
      Rmk: Boolean(row.Rmk),
      ArcelikLg: Boolean(row.ArcelikLg),
      Aygaz: Boolean(row.Aygaz),
      eski: { ServisBirimKodu: row.ServisBirimKodu, TseId: row.StandartNumarasi }
    });
  };

  const belgeKaydet = () => {
    const veri = belgeDialog;
    if (!veri.servis) {
      setNotice({ type: 'warning', message: 'Servis seçiniz.' });
      return;
    }
    if (!veri.standartlar.length) {
      setNotice({ type: 'warning', message: 'Standart seçiniz.' });
      return;
    }
    if (!veri.tarih) {
      setNotice({ type: 'warning', message: 'Belge son geçerlilik tarihi giriniz.' });
      return;
    }
    if (!FIRMA_ALANLARI.some(([alan]) => veri[alan])) {
      setNotice({ type: 'warning', message: 'En az bir firma seçiniz.' });
      return;
    }

    setLoading(true);
    axios.post('TseHybSurecKaydet', {
      ServisBirimKodu: veri.servis.BirimKodu,
      Standartlar: veri.standartlar.map((item) => item.Standart),
      GecerlilikTarihi: format(veri.tarih, 'yyyy-MM-dd'),
      Arcelik: veri.Arcelik,
      Rmk: veri.Rmk,
      ArcelikLg: veri.ArcelikLg,
      Aygaz: veri.Aygaz,
      EskiServisBirimKodu: veri.eski ? veri.eski.ServisBirimKodu : null,
      EskiTseId: veri.eski ? veri.eski.TseId : null
    })
      .then((response) => {
        const sonuc = response.data || {};
        if (String(sonuc.ErrorCode) === '0') {
          setNotice({ type: 'success', message: sonuc.ErrorDescription || 'Kayıt başarılı.' });
          setBelgeDialog(null);
          veriDegisti();
        } else {
          setNotice({ type: 'error', message: sonuc.ErrorDescription || 'Kayıt yapılamadı.' });
        }
      })
      .catch(() => setNotice({ type: 'error', message: 'Kayıt yapılamadı.' }))
      .finally(() => setLoading(false));
  };

  const belgeSil = () => {
    const hedef = belgeSilinecek;
    setLoading(true);
    axios.post('TseHybSurecSil', {
      ServisBirimKodu: hedef.ServisBirimKodu,
      TseId: hedef.StandartNumarasi
    })
      .then((response) => {
        const sonuc = response.data || {};
        if (String(sonuc.ErrorCode) === '0') {
          setNotice({ type: 'success', message: sonuc.ErrorDescription || 'Kayıt silindi.' });
          setBelgeSilinecek(null);
          veriDegisti();
        } else {
          setNotice({ type: 'error', message: sonuc.ErrorDescription || 'Kayıt silinemedi.' });
        }
      })
      .catch(() => setNotice({ type: 'error', message: 'Kayıt silinemedi.' }))
      .finally(() => setLoading(false));
  };

  // Rapor satirindan acilir. EsikId dolu ise mevcut esik duzenlenir; 0 ise
  // (satir sadece belge verisinden geliyor) satirin firma ve standardi
  // secili gelen bos bir esik formu acilir.
  const esikDialogAc = (row) => {
    if (!row) {
      setEsikDialog({ ...BOS_ESIK });
      return;
    }
    setEsikDialog({
      ...BOS_ESIK,
      Id: row.EsikId || 0,
      sirket: companyOptions.find((item) => item.ID === row.SirketId) || null,
      standart: standartOptions.find((item) => item.Standart === row.Standart)
        || { Standart: row.Standart, StandartAciklama: row.StandartAdi || '' },
      standartAdi: row.StandartAdi || '',
      BolgeSayisi: row.BolgeSayisi ?? 0,
      BolgeBasinaMinimum: row.BolgeBasinaMinimum ?? 0,
      ToplamMinimum: row.ToplamMinimum ?? 0
    });
  };

  const esikKaydet = () => {
    const veri = esikDialog;
    if (!veri.sirket) {
      setNotice({ type: 'warning', message: 'Firma seçiniz.' });
      return;
    }
    if (!veri.standart) {
      setNotice({ type: 'warning', message: 'Standart seçiniz.' });
      return;
    }

    setLoading(true);
    axios.post('TseHybEsikKaydet', {
      Id: veri.Id || 0,
      SirketId: veri.sirket.ID,
      Standart: veri.standart.Standart,
      StandartAdi: veri.standartAdi || veri.standart.StandartAciklama || '',
      BolgeSayisi: Number(veri.BolgeSayisi) || 0,
      BolgeBasinaMinimum: Number(veri.BolgeBasinaMinimum) || 0,
      ToplamMinimum: Number(veri.ToplamMinimum) || 0
    })
      .then((response) => {
        const sonuc = response.data || {};
        if (String(sonuc.ErrorCode) === '0') {
          setNotice({ type: 'success', message: sonuc.ErrorDescription || 'Kayıt başarılı.' });
          setEsikDialog(null);
          loadReport();
        } else {
          setNotice({ type: 'error', message: sonuc.ErrorDescription || 'Kayıt yapılamadı.' });
        }
      })
      .catch(() => setNotice({ type: 'error', message: 'Kayıt yapılamadı.' }))
      .finally(() => setLoading(false));
  };

  const esikSil = () => {
    setLoading(true);
    axios.post('TseHybEsikSil', { Id: esikSilinecek.EsikId })
      .then((response) => {
        const sonuc = response.data || {};
        if (String(sonuc.ErrorCode) === '0') {
          setNotice({ type: 'success', message: sonuc.ErrorDescription || 'Kayıt silindi.' });
          setEsikSilinecek(null);
          loadReport();
        } else {
          setNotice({ type: 'error', message: sonuc.ErrorDescription || 'Kayıt silinemedi.' });
        }
      })
      .catch(() => setNotice({ type: 'error', message: 'Kayıt silinemedi.' }))
      .finally(() => setLoading(false));
  };

  return (
    <Page title="TSE HYB Süreçleri">
      <Backdrop sx={styles.backdrop} open={loading}><CircularProgress color="inherit" /></Backdrop>
      <Container maxWidth={false}>
        <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
          <Tab label="TSE-HYB Süreçleri" />
          <Tab label="Raporlar" />
        </Tabs>
        {tab === 1 ? (
          <Card>
            <CardHeader
              title="TSE-HYB Firma ve Coğrafi Bölge Bazlı Standart Sayıları"
              subheader={`Bölge kolonları mevcut servis sayısını gösterir; yönetmelik eşiğinin altındakiler kırmızıdır. Tüm veriyi kapsar, diğer sekmedeki filtrelerden etkilenmez.${
                editMode
                  ? ' "Min." kolonları ve "Eksik" durumu yönetmelik eşiklerinden hesaplanır; eşikleri satır sonundaki İşlem kolonundan yönetebilirsiniz.'
                  : ''}`}
              action={duzenleyebilir && (
                <FormControlLabel
                  control={<Switch checked={editMode} onChange={handleEditModeChange} color="warning" />}
                  label="Düzenleme modu"
                />
              )}
            />
            <CardContent>
              <Box sx={styles.actions}>
                <Box sx={styles.actionButtonsRow}>
                  {editMode && (
                    // Raporda hic satiri olmayan firma-standart cifti icin;
                    // raporda gorunen satirlarin esigi satir ikonlarindan yonetilir.
                    <Tooltip title="Raporda satırı bulunmayan bir firma-standart çifti için eşik ekler">
                      <span>
                        <Button
                          sx={[styles.actionButton, styles.orangeOutlineButton]}
                          variant="outlined"
                          onClick={() => esikDialogAc(null)}
                          startIcon={<AddIcon />}
                        >
                          Yeni Eşik
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Raporu Excel dosyası olarak indirir">
                    <span>
                      <Button
                        sx={[styles.actionButton, styles.excelButton]}
                        style={{ color: '#fff' }}
                        variant="contained"
                        onClick={exportReport}
                        startIcon={<GetAppIcon />}
                        disabled={!reportRows.length}
                      >
                        Excel İndir
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Typography sx={styles.resultCount} variant="body2" color="textSecondary">
                {reportRows.length} kayıt bulundu
              </Typography>
              <Box sx={styles.tableContainer}>
                <table style={styles.table}>
                  <thead><tr>
                    {RAPOR_COLUMNS.map(([, title]) => (
                      <Box component="th" sx={[styles.cell, styles.header]} key={title}>{title}</Box>
                    ))}
                    {editMode && <Box component="th" sx={[styles.islemCell, styles.header]}>İşlem</Box>}
                  </tr></thead>
                  <tbody>{reportRows.map((row, index) => (
                    <Box
                      component="tr"
                      sx={styles.row}
                      key={`${row.Firma}-${row.Standart}-${index}`}
                      // Inline stil: satir seritleme (nth-of-type) uzerine yazsin diye
                      style={row.Durum === 'Eksik'
                        ? { backgroundColor: '#fdecea', boxShadow: 'inset 4px 0 0 0 #d32f2f' }
                        : undefined}
                    >
                      {RAPOR_COLUMNS.map(([field]) => {
                        // Yonetmelik esiginin altindaki degerler kirmizi gosterilir
                        const bolgeEksik = BOLGE_COLUMNS.some(([bolge]) => bolge === field)
                          && (row[field] ?? 0) < (row.BolgeBasinaMinimum ?? 0);
                        const toplamEksik = (field === 'Toplam' || field === 'ToplamFark')
                          && (row.ToplamFark ?? 0) < 0;
                        const durumEksik = field === 'Durum' && row.Durum === 'Eksik';

                        if (durumEksik) {
                          return (
                            <Box component="td" sx={styles.cell} key={field}>
                              <Chip size="small" label="Eksik" color="error" />
                            </Box>
                          );
                        }

                        return (
                          <Box
                            component="td"
                            sx={styles.cell}
                            key={field}
                            style={
                              bolgeEksik || toplamEksik
                                ? { color: '#d32f2f', fontWeight: 700 }
                                : undefined
                            }
                          >
                            {row[field]}
                          </Box>
                        );
                      })}
                      {editMode && (
                        // Satirin "Min." kolonlarinin kaynagi olan yonetmelik
                        // esigi. EsikId 0 ise bu cift icin esik hic tanimlanmamis.
                        <Box component="td" sx={styles.islemCell}>
                          {row.EsikId > 0 ? (
                            <>
                              <Tooltip title="Yönetmelik eşiğini düzenle">
                                <IconButton size="small" onClick={() => esikDialogAc(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Yönetmelik eşiğini sil">
                                <IconButton size="small" color="error" onClick={() => setEsikSilinecek(row)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Bu firma-standart çifti için yönetmelik eşiği tanımla">
                              <IconButton size="small" onClick={() => esikDialogAc(row)}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}</tbody>
                </table>
                {!reportRows.length && (
                  <Typography align="center" color="textSecondary" style={{ padding: 24 }}>
                    Gösterilecek kayıt bulunamadı.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader
            title="TSE-HYB Süreçleri"
            action={duzenleyebilir && (
              <FormControlLabel
                control={<Switch checked={editMode} onChange={handleEditModeChange} color="warning" />}
                label="Düzenleme modu"
              />
            )}
          />
          <CardContent>
            <Box sx={styles.filters}>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Bölge"
                  options={regionOptions}
                  value={region}
                  onChange={setRegion}
                  getOptionLabel={(option) => option}
                  getOptionKey={(option) => option}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Servis"
                  options={serviceOptions}
                  value={serviceSelection}
                  onChange={setServiceSelection}
                  getOptionLabel={(option) => option.Dsc}
                  getOptionKey={(option) => option.BirimKodu}
                  disabled={!region.length}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="TSE Standartlar"
                  options={standartOptions}
                  value={standartSelection}
                  onChange={setStandartSelection}
                  getOptionLabel={(option) => `${option.Standart} - ${option.StandartAciklama}`}
                  getOptionKey={(option) => option.Standart}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Şirket"
                  options={companyOptions}
                  value={companySelection}
                  onChange={setCompanySelection}
                  getOptionLabel={(option) => option.ACIKLAMA}
                  getOptionKey={(option) => option.ID}
                />
              </Box>
            </Box>
            <Box sx={styles.actions}>
              <Box sx={styles.actionButtonsRow}>
                {editMode && (
                  <Tooltip title="Yeni belge kaydı ekler">
                    <span>
                      <Button
                        sx={[styles.actionButton, styles.orangeOutlineButton]}
                        variant="outlined"
                        onClick={() => belgeDialogAc(null)}
                        startIcon={<AddIcon />}
                      >
                        Yeni Kayıt
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Görüntülenen kayıtları Excel dosyası olarak indirir">
                  <span>
                    <Button
                      sx={[styles.actionButton, styles.excelButton]}
                      style={{ color: '#fff' }}
                      variant="contained"
                      onClick={exportRows}
                      startIcon={<GetAppIcon />}
                      disabled={!rows.length}
                    >
                      Excel İndir
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Seçili filtrelerle arama yapar">
                  <span>
                    <Button
                      sx={[styles.actionButton, styles.orangeButton]}
                      style={{ color: '#fff' }}
                      variant="contained"
                      onClick={loadRows}
                      startIcon={<SearchIcon />}
                      disabled={loading}
                    >
                      Ara
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <TextField
                sx={styles.searchField}
                variant="outlined"
                size="small"
                label="Arama"
                placeholder="Servis kodu, adı veya unvanı..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Aramayı temizle" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Typography sx={styles.resultCount} variant="body2" color="textSecondary">
              {filteredRows.length} kayıt bulundu
            </Typography>
            <Box sx={styles.tableContainer}>
              <table style={styles.table}>
                <thead><tr>
                  {LISTE_COLUMNS.map(([, title]) => (
                    <Box component="th" sx={[styles.cell, styles.header]} key={title}>{title}</Box>
                  ))}
                  {editMode && <Box component="th" sx={[styles.islemCell, styles.header]}>İşlem</Box>}
                </tr></thead>
                <tbody>{filteredRows.map((row, index) => (
                  <Box component="tr" sx={styles.row} key={`${row.ServisKodu}-${row.StandartNumarasi}-${index}`}>
                    {LISTE_COLUMNS.map(([field]) => (
                      <Box component="td" sx={styles.cell} key={field}>{row[field]}</Box>
                    ))}
                    {editMode && (
                      <Box component="td" sx={styles.islemCell}>
                        <Tooltip title="Düzenle">
                          <IconButton size="small" onClick={() => belgeDialogAc(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton size="small" color="error" onClick={() => setBelgeSilinecek(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                ))}</tbody>
              </table>
              {!filteredRows.length && <Typography align="center" color="textSecondary" style={{ padding: 24 }}>Gösterilecek kayıt bulunamadı.</Typography>}
            </Box>
          </CardContent>
        </Card>
        )}
      </Container>

      {/* Belge kaydi: ekleme ve duzenleme ayni dialog. Bolge, bolge
          yoneticiligi ve unvan servisten turedigi icin girilmiyor. */}
      <Dialog open={Boolean(belgeDialog)} onClose={() => setBelgeDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{belgeDialog?.eski ? 'Belge Kaydını Düzenle' : 'Yeni Belge Kaydı'}</DialogTitle>
        <DialogContent>
          <Box sx={styles.dialogAlan}>
            <Autocomplete
              size="small"
              options={allServiceOptions}
              value={belgeDialog?.servis || null}
              getOptionLabel={(option) => option.Dsc || ''}
              isOptionEqualToValue={(option, val) => option.BirimKodu === val.BirimKodu}
              onChange={(event, secim) => setBelgeDialog({ ...belgeDialog, servis: secim })}
              renderInput={(params) => <TextField {...params} label="Servis" variant="outlined" />}
            />
          </Box>
          <Box sx={styles.dialogAlan}>
            <Autocomplete
              size="small"
              freeSolo
              multiple={!belgeDialog?.eski}
              options={standartOptions}
              value={belgeDialog?.eski
                ? (belgeDialog?.standartlar?.[0] || null)
                : (belgeDialog?.standartlar || [])}
              getOptionLabel={standartEtiket}
              isOptionEqualToValue={(option, val) => standartNesne(option)?.Standart === standartNesne(val)?.Standart}
              onChange={(event, secim) => setBelgeDialog({
                ...belgeDialog,
                // Duzenlemede tek standart; eklemede coklu secim tek seferde
                // birden fazla satir olusturur. freeSolo oldugu icin listede
                // olmayan bir standart da yazilabiliyor.
                standartlar: belgeDialog.eski
                  ? [standartNesne(secim)].filter(Boolean)
                  : (secim || []).map(standartNesne).filter(Boolean)
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={belgeDialog?.eski ? 'Standart' : 'Standartlar'}
                  variant="outlined"
                  helperText={belgeDialog?.eski
                    ? 'Listede yoksa doğrudan yazabilirsiniz (örn. TS 12345).'
                    : 'Birden fazla seçebilirsiniz; her biri ayrı kayıt olur. Listede olmayanı yazıp Enter\'a basın.'}
                />
              )}
            />
            {tanimsizStandartlar.length > 0 && (
              <Typography variant="caption" color="textSecondary" component="div" sx={{ marginTop: 0.5 }}>
                {tanimsizStandartlar.join(', ')} yönetmelik eşiği tablosunda tanımlı değil.
                Belge kaydı girilebilir, ancak standart adı boş görünür ve rapora
                girmesi için Raporlar sekmesinden eşik tanımlamanız gerekir.
              </Typography>
            )}
          </Box>
          <Box sx={styles.dialogAlan}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
              <DatePicker
                label="Belge Son Geçerlilik Tarihi"
                format="dd.MM.yyyy"
                value={belgeDialog?.tarih || null}
                onChange={(date) => setBelgeDialog({ ...belgeDialog, tarih: date })}
                slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={styles.dialogAlan}>
            <Typography variant="body2" color="textSecondary">Firmalar</Typography>
            <FormGroup row>
              {FIRMA_ALANLARI.map(([alan, etiket]) => (
                <FormControlLabel
                  key={alan}
                  control={(
                    <Checkbox
                      size="small"
                      checked={Boolean(belgeDialog?.[alan])}
                      onChange={(event) => setBelgeDialog({ ...belgeDialog, [alan]: event.target.checked })}
                    />
                  )}
                  label={etiket}
                />
              ))}
            </FormGroup>
          </Box>
          {belgeDialog?.servis && (
            <Box sx={styles.turetilenBilgi}>
              Bölge: <strong>{belgeDialog.servis.TrBolgesi || '—'}</strong>
              {'  ·  '}
              Bölge Yöneticiliği: <strong>{belgeDialog.servis.BolgeYoneticiligiAdi || '—'}</strong>
              <Typography variant="caption" component="div" color="textSecondary">
                Seçilen servisten otomatik belirlenir.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBelgeDialog(null)}>İptal</Button>
          <Button
            sx={[styles.actionButton, styles.orangeButton]}
            style={{ color: '#fff' }}
            variant="contained"
            onClick={belgeKaydet}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(belgeSilinecek)} onClose={() => setBelgeSilinecek(null)}>
        <DialogTitle>Kayıt silinsin mi?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{belgeSilinecek?.ServisKodu} - {belgeSilinecek?.ServisAdi}</strong> servisinin{' '}
            <strong>{belgeSilinecek?.StandartNumarasi}</strong> belge kaydı silinecek.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBelgeSilinecek(null)}>Vazgeç</Button>
          <Button
            sx={[styles.actionButton, styles.dangerButton]}
            style={{ color: '#fff' }}
            variant="contained"
            onClick={belgeSil}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yonetmelik esigi */}
      <Dialog open={Boolean(esikDialog)} onClose={() => setEsikDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{esikDialog?.Id ? 'Yönetmelik Eşiğini Düzenle' : 'Yeni Yönetmelik Eşiği'}</DialogTitle>
        <DialogContent>
          <Box sx={styles.dialogAlan}>
            <Autocomplete
              size="small"
              options={companyOptions}
              value={esikDialog?.sirket || null}
              getOptionLabel={(option) => option.ACIKLAMA || ''}
              isOptionEqualToValue={(option, val) => option.ID === val.ID}
              onChange={(event, secim) => setEsikDialog({ ...esikDialog, sirket: secim })}
              renderInput={(params) => <TextField {...params} label="Firma" variant="outlined" />}
            />
          </Box>
          <Box sx={styles.dialogAlan}>
            <Autocomplete
              size="small"
              freeSolo
              options={standartOptions}
              value={esikDialog?.standart || null}
              getOptionLabel={standartEtiket}
              isOptionEqualToValue={(option, val) => standartNesne(option)?.Standart === standartNesne(val)?.Standart}
              onChange={(event, secim) => {
                const nesne = standartNesne(secim);
                setEsikDialog({
                  ...esikDialog,
                  standart: nesne,
                  // Listeden secildiyse ad otomatik dolar; serbest yazilan yeni
                  // standartta kullanici kendisi girer.
                  standartAdi: nesne?.StandartAciklama || esikDialog.standartAdi || ''
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Standart"
                  variant="outlined"
                  helperText="Listede yoksa doğrudan yazabilirsiniz (örn. TS 12345)."
                />
              )}
            />
          </Box>
          <Box sx={styles.dialogAlan}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Standart Adı"
              value={esikDialog?.standartAdi || ''}
              onChange={(event) => setEsikDialog({ ...esikDialog, standartAdi: event.target.value })}
              helperText="Raporda ve listede bu ad görünür."
            />
          </Box>
          {[
            ['BolgeSayisi', 'Bölge Sayısı'],
            ['BolgeBasinaMinimum', 'Bölge Başına Minimum Servis Sayısı'],
            ['ToplamMinimum', 'Toplam Minimum Servis Sayısı']
          ].map(([alan, etiket]) => (
            <Box sx={styles.dialogAlan} key={alan}>
              <TextField
                fullWidth
                size="small"
                type="number"
                variant="outlined"
                label={etiket}
                value={esikDialog?.[alan] ?? 0}
                onChange={(event) => setEsikDialog({ ...esikDialog, [alan]: event.target.value })}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEsikDialog(null)}>İptal</Button>
          <Button
            sx={[styles.actionButton, styles.orangeButton]}
            style={{ color: '#fff' }}
            variant="contained"
            onClick={esikKaydet}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(esikSilinecek)} onClose={() => setEsikSilinecek(null)}>
        <DialogTitle>Eşik kaydı silinsin mi?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{esikSilinecek?.Firma}</strong> firmasının{' '}
            <strong>{esikSilinecek?.Standart}</strong> yönetmelik eşiği silinecek.
            Rapordaki minimum değerler bu kayıttan geliyor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEsikSilinecek(null)}>Vazgeç</Button>
          <Button
            sx={[styles.actionButton, styles.dangerButton]}
            style={{ color: '#fff' }}
            variant="contained"
            onClick={esikSil}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)}><Alert variant={notice?.type || 'default'} message={notice?.message || ''} onClose={() => setNotice(null)} /></Snackbar>
    </Page>
  );
};

export default TseHybSurecleriView;
